import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useParametres } from '../../hooks/useParametres';

const VALID_LOGIN = 'WISSEM';
const VALID_PWD = 'HAZ-221178';

export default function LoginPage() {
  const navigate = useNavigate();
  const { settings } = useParametres();
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const isAuth =
      sessionStorage.getItem('readyia_auth') ||
      localStorage.getItem('readyia_auth');

    if (isAuth === 'true') {
      sessionStorage.setItem('readyia_auth', 'true');
      navigate('/dashboard', { replace: true });
    }

    // Restore remembered credentials
    const savedLogin = localStorage.getItem('readyia_remember_login');
    const savedPwd = localStorage.getItem('readyia_remember_pwd');
    if (savedLogin && savedPwd) {
      setLogin(savedLogin);
      setPassword(savedPwd);
      setRememberMe(true);
    }

    const timer = setTimeout(() => setMounted(true), 100);
    return () => clearTimeout(timer);
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!login.trim() || !password.trim()) {
      setError('Veuillez remplir tous les champs');
      return;
    }

    setIsLoading(true);

    try {
      // Simulate async login request
      await new Promise((resolve) => setTimeout(resolve, 800));

      if (login.toUpperCase() === VALID_LOGIN && password === VALID_PWD) {
        sessionStorage.setItem('readyia_auth', 'true');
        sessionStorage.setItem('readyia_user', login);

        if (rememberMe) {
          localStorage.setItem('readyia_auth', 'true');
          localStorage.setItem('readyia_remember_login', login);
          localStorage.setItem('readyia_remember_pwd', password);
        } else {
          localStorage.removeItem('readyia_auth');
          localStorage.removeItem('readyia_remember_login');
          localStorage.removeItem('readyia_remember_pwd');
        }

        navigate('/dashboard', { replace: true });
      } else {
        setError('Identifiant ou mot de passe incorrect');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  const logoUrl = settings?.logo_url || 'https://static.readdy.ai/image/332d61bd55ff2bc7860ffa3525be2f43/8886ee66528d0e71169012dc527d6a17.png';

  return (
    <div
      className="min-h-screen w-full flex relative overflow-hidden"
      style={{
        background:
          'linear-gradient(135deg, #f8fafc 0%, #e8f4f8 50%, #f0f4ff 100%)',
      }}
    >
      {/* Decorative background elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(0,0,0,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.1) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        ></div>

        {/* Floating shapes */}
        <div className="absolute top-20 left-20 w-72 h-72 bg-sky-200/20 rounded-full blur-3xl animate-pulse"></div>
        <div
          className="absolute bottom-20 right-20 w-96 h-96 bg-sky-300/15 rounded-full blur-3xl"
          style={{ animation: 'pulse 4s ease-in-out infinite' }}
        ></div>
        <div
          className="absolute top-1/2 left-1/3 w-48 h-48 bg-sky-100/20 rounded-full blur-2xl"
          style={{ animation: 'pulse 6s ease-in-out infinite' }}
        ></div>

        {/* Accounting decorative icons */}
        <div
          className={`absolute top-[15%] left-[8%] transition-all duration-1000 ${
            mounted ? 'opacity-[0.06] translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <i className="ri-calculator-line text-7xl text-gray-900"></i>
        </div>
        <div
          className={`absolute top-[25%] right-[10%] transition-all duration-1000 delay-200 ${
            mounted ? 'opacity-[0.05] translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <i className="ri-file-chart-line text-8xl text-gray-900"></i>
        </div>
        <div
          className={`absolute bottom-[20%] left-[12%] transition-all duration-1000 delay-400 ${
            mounted ? 'opacity-[0.05] translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <i className="ri-pie-chart-line text-6xl text-gray-900"></i>
        </div>
        <div
          className={`absolute bottom-[30%] right-[8%] transition-all duration-1000 delay-300 ${
            mounted ? 'opacity-[0.04] translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <i className="ri-money-dollar-circle-line text-7xl text-gray-900"></i>
        </div>
        <div
          className={`absolute top-[60%] left-[5%] transition-all duration-1000 delay-500 ${
            mounted ? 'opacity-[0.04] translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <i className="ri-bank-line text-6xl text-gray-900"></i>
        </div>
        <div
          className={`absolute top-[10%] left-[45%] transition-all duration-1000 delay-100 ${
            mounted ? 'opacity-[0.04] translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <i className="ri-bar-chart-grouped-line text-5xl text-gray-900"></i>
        </div>
        <div
          className={`absolute bottom-[10%] left-[40%] transition-all duration-1000 delay-600 ${
            mounted ? 'opacity-[0.04] translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <i className="ri-receipt-line text-6xl text-gray-900"></i>
        </div>
      </div>

      {/* Center content */}
      <div className="flex-1 flex items-center justify-center px-4 py-8 relative z-10">
        <div
          className={`w-full max-w-md transition-all duration-700 ${
            mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {/* Logo section */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center mb-6">
              <img
                src={logoUrl}
                alt="Logo"
                className="h-24 w-auto object-contain drop-shadow-md brightness-110 contrast-105"
              />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
              Bienvenue
            </h1>
            <p className="text-sm text-gray-500 mt-2">
              Connectez-vous à votre espace comptable
            </p>
          </div>

          {/* Login card */}
          <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-xl shadow-gray-200/50 border border-white/60 p-8">
            {/* Decorative top bar */}
            <div className="flex items-center gap-2 mb-8 pb-6 border-b border-gray-100">
              <div className="w-9 h-9 bg-sky-50 rounded-lg flex items-center justify-center">
                <i className="ri-shield-keyhole-line text-sky-600 text-lg"></i>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  Connexion Sécurisée
                </p>
                <p className="text-xs text-gray-400">
                  Accès réservé au personnel autorisé
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Login field */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                  Identifiant
                </label>
                <div className="relative">
                  <div className="absolute left-0 top-0 bottom-0 w-11 flex items-center justify-center pointer-events-none">
                    <i className="ri-user-3-line text-gray-400 text-lg"></i>
                  </div>
                  <input
                    type="text"
                    value={login}
                    onChange={(e) => {
                      setLogin(e.target.value);
                      setError('');
                    }}
                    placeholder="Entrez votre identifiant"
                    className="w-full pl-11 pr-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-400 focus:bg-white transition-all"
                    autoComplete="username"
                    autoFocus
                  />
                </div>
              </div>

              {/* Password field */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                  Mot de passe
                </label>
                <div className="relative">
                  <div className="absolute left-0 top-0 bottom-0 w-11 flex items-center justify-center pointer-events-none">
                    <i className="ri-lock-2-line text-gray-400 text-lg"></i>
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setError('');
                    }}
                    placeholder="Entrez votre mot de passe"
                    className="w-full pl-11 pr-12 py-3 bg-gray-50/80 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-400 focus:bg-white transition-all"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-0 top-0 bottom-0 w-12 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                  >
                    <i
                      className={`${
                        showPassword ? 'ri-eye-off-line' : 'ri-eye-line'
                      } text-lg`}
                    ></i>
                  </button>
                </div>
              </div>

              {/* Remember me */}
              <div className="flex items-center">
                <button
                  type="button"
                  onClick={() => setRememberMe(!rememberMe)}
                  className="flex items-center gap-2.5 cursor-pointer group"
                >
                  <div
                    className={`w-[18px] h-[18px] rounded-md border-2 flex items-center justify-center transition-all ${
                      rememberMe
                        ? 'bg-sky-500 border-sky-500'
                        : 'border-gray-300 group-hover:border-gray-400'
                    }`}
                  >
                    {rememberMe && (
                      <i className="ri-check-line text-white text-xs"></i>
                    )}
                  </div>
                  <span className="text-sm text-gray-600 select-none">
                    Se souvenir de moi
                  </span>
                </button>
              </div>

              {/* Error message */}
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-100 rounded-xl">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className="ri-error-warning-line text-red-500"></i>
                  </div>
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              {/* Submit button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3.5 bg-gray-900 text-white rounded-xl text-sm font-semibold hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900/30 focus:ring-offset-2 transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Connexion en cours...
                  </>
                ) : (
                  <>
                    <span>Se connecter</span>
                    <i className="ri-arrow-right-line"></i>
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Footer info */}
          <div className="mt-6 text-center">
            <div className="flex items-center justify-center gap-4 text-xs text-gray-400">
              <span className="flex items-center gap-1">
                <i className="ri-shield-check-line"></i>
                Chiffré SSL
              </span>
              <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
              <span className="flex items-center gap-1">
                <i className="ri-lock-line"></i>
                Données sécurisées
              </span>
            </div>
            <p className="text-xs text-gray-300 mt-4">
              &copy; {new Date().getFullYear()} XtremeDigital — Gestion Comptable
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
